import Modal from '../../components/ui/Modal'
import { 
	GiBiceps as StrongIcon, 
	GiBroadheadArrow as SharpIcon, 
	GiArmorUpgrade as LongIcon 
} from 'react-icons/gi'

interface PasswordInfoProps {
	show: boolean,
	setShow: (open: boolean) => void
}
const PasswordInfo = ({ show, setShow }: PasswordInfoProps) => {
	return (
		<Modal show={show} onHide={() => setShow(false)} size="sm">
			<Modal.Header onHide={() => setShow(false)}>Create a secure password</Modal.Header>

			<Modal.Body>
				<p>Please ensure your password meets the following criteria:</p>

				<ul className="ml-8 mt-2 list-disc">
					<li>Minimum of 8 characters</li>
					<li>At least 1 uppercase letter</li>
					<li>At least 1 lowercase letter</li>
					<li>At least 1 number</li>
				</ul>
			</Modal.Body>
		</Modal>
	)
}

interface BiologicalSexInfoProps {
	show: boolean,
	setShow: (open: boolean) => void
}

const BiologicalSexInfo = ({ show, setShow }: BiologicalSexInfoProps) => {
	return (
		<Modal show={show} onHide={() => setShow(false)} size="sm">
			<Modal.Header onHide={() => setShow(false)}>
				Biological Sex vs Gender: What's the Difference?
			</Modal.Header>

			<Modal.Body>
				<p>
					Biological Sex refers to the physical characteristics you're born with, including your
					reproductive organs, chromosomes (XX for females, XY for males), and hormone levels.{' '}
					<br />
					<br />
					It’s something that’s assigned at birth based on these traits, categorising individuals as
					male or female.
					<br />
					<br />
					On the other hand, Gender is more about how you identify and express yourself in society.
					It’s shaped by social and cultural factors, and can include categories like man, woman,
					non-binary, or genderqueer.
					<br />
					<br />
					While biological sex is rooted in your physical attributes, gender is about your personal
					identity, which might not necessarily match your biological sex. So, while biological sex
					is largely determined by anatomy and genetics, gender is more about who you feel you are
					and how you express yourself to the world.
				</p>
			</Modal.Body>
		</Modal>
	)
}

interface PersonaGoalInfoProps {
	show: boolean,
	setShow: (open: boolean) => void
}

const PersonaGoalInfo = ({ show, setShow }: PersonaGoalInfoProps) => {
	return (
		<Modal show={show} onHide={() => setShow(false)} size="sm">
			<Modal.Header onHide={() => setShow(false)}>
				Wellbeing goals{' '}
				<LongIcon className="mr-1 inline align-text-bottom text-3xl text-peach-600" />
				<SharpIcon className="mr-1 inline -rotate-90 align-text-bottom text-3xl text-green-600" />
				<StrongIcon className="mr-1 inline align-text-bottom text-4xl text-orange-600" />
			</Modal.Header>

			<Modal.Body>
				<div className="space-y-2">
					<p className="mb-2 text-xl text-peach-600">
						<LongIcon className="mr-1 inline align-text-bottom text-3xl" />
						<b>Live Longer</b>
					</p>
					<p>
						<b>"I want to make sure I’m healthy"</b>
					</p>
					<p>
						This goal is all about looking after your long-term health. By focusing on prevention,
						balanced nutrition, and regular exercise, you’ll keep your body in top shape as you age.
						We’ll guide you in reducing the risk of chronic illnesses, boosting your energy levels,
						and promoting overall wellness, so you can enjoy life to the fullest for years to come.
					</p>
				</div>

				<hr className="my-10" />

				<div className="space-y-2">
					<p className="text-xl text-green-600">
						<SharpIcon className="mr-1 inline -rotate-90 align-text-bottom text-3xl" />
						<b>Live Sharper </b>
					</p>
					<p>
						<b>"I want to improve mental performance"</b>
					</p>
					<p>
						Staying mentally sharp is key to performing at your best. This goal is designed to help
						you boost your focus, improve memory, and enhance overall brain function. Whether you’re
						aiming to excel at work or simply stay mentally agile, we’ll create a personalised plan
						to keep your mind clear and sharp, using nutrition, brain training, and stress
						management techniques.
					</p>
				</div>

				<hr className="my-10" />

				<div className="mb-6 space-y-2">
					<p className="text-xl text-orange-600">
						<b>
							<StrongIcon className="mr-1 inline align-text-bottom text-4xl" /> Live Stronger
						</b>
					</p>
					<p>
						<b>"I want to push my body"</b>
					</p>
					<p>
						If you’re looking to challenge your body and build strength, this goal is perfect for
						you. Whether you're an athlete or simply want to push your fitness further, we’ll help
						you focus on strength, endurance, and recovery. With personalised workouts, nutritional
						guidance, and recovery strategies, you’ll build a body that’s stronger, more resilient,
						and ready for anything.
					</p>
				</div>
			</Modal.Body>
		</Modal>
	)
}

export { PasswordInfo, BiologicalSexInfo, PersonaGoalInfo }
